import { KillChainCategory, type StudyP4E2ERowSelectDto, type StudyThreatTargetSelectDto } from '@@types/api.types';

export type MeiaP4E2eColumnKey = 'sensors' | 'c2' | 'effectors' | 'assess' | 'targets';
type MeiaP4E2eFunctionColumnKey = Exclude<MeiaP4E2eColumnKey, 'targets'>;

export type MeiaP4E2eColumnDef = {
  key: MeiaP4E2eColumnKey;
  title: string;
  color: string;
};

export const meiaP4E2eColumnDefs: ReadonlyArray<MeiaP4E2eColumnDef> = [
  { color: '#2563eb', key: 'sensors', title: 'Sensors & ISR' },
  { color: '#8b5cf6', key: 'c2', title: 'C2 & Decision' },
  { color: '#ef4444', key: 'effectors', title: 'Effectors' },
  { color: '#f59e0b', key: 'assess', title: 'Assess' },
  { color: '#dc2626', key: 'targets', title: 'Targets' },
];

export type MeiaP4E2eFunctionCard = {
  id: string;
  label: string;
  role: StudyP4E2ERowSelectDto['threadRole'] | 'MULTI';
  sub: string;
  technologyId: string;
  threadIds: string[];
};

export type MeiaP4E2eTargetCard = {
  id: string;
  label: string;
  priority?: string | null;
  sub: string;
  threadIds: string[];
};

export type MeiaP4E2eCardsByColumn = {
  assess: MeiaP4E2eFunctionCard[];
  c2: MeiaP4E2eFunctionCard[];
  effectors: MeiaP4E2eFunctionCard[];
  sensors: MeiaP4E2eFunctionCard[];
  targets: MeiaP4E2eTargetCard[];
};

const ASSESS_PHASES = new Set(['ASSESS', 'BDA', 'VALIDATE']);
const C2_PHASES = new Set([
  'C2',
  'COMMAND',
  'DECIDE',
  'DIRECT',
  'DISSEMINATE',
  'DISTRIBUTE',
  'FORECAST',
  'PLAN',
  'SOURCE',
  'SUSTAIN',
  'TARGET',
  'UNDERSTAND',
]);
const EFFECTOR_PHASES = new Set(['ACTION', 'DEFEAT', 'DELIVER', 'EFFECT', 'ENGAGE']);
const SENSOR_PHASES = new Set(['COLLECT', 'DETECT', 'FIND', 'FIX', 'PROCESS', 'SENSE', 'TRACK']);

const normalizeStageName = (value?: string | null): string => value?.trim().toUpperCase() ?? '';
const normalizeTargetCode = (value?: string | null): string => value?.trim() ?? '';

export const columnKeyForKillChainStage = (params: {
  swimlanePhaseName?: string | null;
  threadKillChainCategory?: KillChainCategory | null;
}): MeiaP4E2eFunctionColumnKey | null => {
  const { swimlanePhaseName, threadKillChainCategory } = params;
  const phaseName = normalizeStageName(swimlanePhaseName);

  if (threadKillChainCategory === KillChainCategory.CYBER && ['ACTION', 'EXPLOIT', 'INSTALL'].includes(phaseName)) {
    return 'effectors';
  }
  if (threadKillChainCategory === KillChainCategory.ISR && phaseName === 'EXPLOIT') return 'sensors';

  if (ASSESS_PHASES.has(phaseName)) return 'assess';
  if (SENSOR_PHASES.has(phaseName)) return 'sensors';
  if (C2_PHASES.has(phaseName)) return 'c2';
  if (EFFECTOR_PHASES.has(phaseName)) return 'effectors';
  return null;
};

type BucketParams = {
  rows: StudyP4E2ERowSelectDto[];
  threatTargets: StudyThreatTargetSelectDto[];
};

export const bucketP4E2eCards = (params: BucketParams): MeiaP4E2eCardsByColumn => {
  const { rows, threatTargets } = params;
  const buckets: MeiaP4E2eCardsByColumn = {
    assess: [],
    c2: [],
    effectors: [],
    sensors: [],
    targets: [],
  };

  const cardsByKey = new Map<string, MeiaP4E2eFunctionCard>();
  const targetCardsByCode = new Map<string, MeiaP4E2eTargetCard>();
  const threatTargetsByCode = new Map(
    threatTargets.map((target) => [normalizeTargetCode(target.threatTargetSystemCode), target]),
  );

  for (const row of rows) {
    const targetCode = normalizeTargetCode(row.expectedThreatTargetSystemCode);
    if (targetCode.length > 0) {
      const existingTarget = targetCardsByCode.get(targetCode);
      if (existingTarget) {
        if (!existingTarget.threadIds.includes(row.threadId)) existingTarget.threadIds.push(row.threadId);
      } else {
        const registeredTarget = threatTargetsByCode.get(targetCode);
        targetCardsByCode.set(targetCode, {
          id: `target:${targetCode}`,
          label: targetCode,
          priority: registeredTarget?.priority ?? null,
          sub: registeredTarget?.location?.trim() || registeredTarget?.alias?.trim() || row.threadName,
          threadIds: [row.threadId],
        });
      }
    }

    const columnKey = columnKeyForKillChainStage({
      swimlanePhaseName: row.swimlanePhaseName,
      threadKillChainCategory: row.threadKillChainCategory,
    });
    if (!columnKey) continue;

    const cardKey = `${columnKey}:${row.technologyId}`;
    const existing = cardsByKey.get(cardKey);
    if (existing) {
      if (!existing.threadIds.includes(row.threadId)) {
        existing.threadIds.push(row.threadId);
        if (existing.role !== row.threadRole) existing.role = 'MULTI';
        existing.sub = `${existing.threadIds.length} threads`;
      }
      continue;
    }

    const card: MeiaP4E2eFunctionCard = {
      id: cardKey,
      label: row.technologyName,
      role: row.threadRole,
      sub: row.threadName,
      technologyId: row.technologyId,
      threadIds: [row.threadId],
    };
    cardsByKey.set(cardKey, card);
    buckets[columnKey].push(card);
  }

  buckets.targets = Array.from(targetCardsByCode.values());

  return buckets;
};

export const countUniqueTechnologies = (rows: StudyP4E2ERowSelectDto[]): number => {
  const ids = new Set<string>();
  for (const row of rows) ids.add(row.technologyId);
  return ids.size;
};

export const countUniqueTasks = (rows: StudyP4E2ERowSelectDto[]): number => {
  const ids = new Set<string>();
  for (const row of rows) ids.add(row.taskId);
  return ids.size;
};
